<template>
    <div class="reservation-form">
        <div class="page-header">
            <div>
                <h2 class="page-title">预约表单管理</h2>
                <p class="page-desc">管理预约表单，支持自定义字段和快速复制</p>
            </div>
            <el-button type="primary" size="large" @click="openAddDialog">
                <el-icon>
                    <Plus />
                </el-icon>
                新增表单
            </el-button>
        </div>

        <!-- 搜索筛选栏 -->
        <div class="filter-bar">
            <el-input v-model="searchKey" placeholder="搜索表单名称" prefix-icon="Search" clearable style="width: 280px"
                @keyup.enter="fetchData" />
            <el-select v-model="statusFilter" placeholder="全部状态" clearable style="width: 120px" @change="fetchData">
                <el-option label="启用" :value="1" />
                <el-option label="禁用" :value="0" />
            </el-select>
            <el-button type="primary" @click="fetchData">查询</el-button>
            <el-button @click="resetSearch">重置</el-button>
        </div>

        <!-- 表单列表 -->
        <div class="form-grid">
            <div v-for="form in formList" :key="form.id" class="form-card">
                <div class="form-header">
                    <div class="form-title">
                        <h3>{{ form.form_name }}</h3>
                        <el-tag v-if="form.is_default === 1" type="success" size="small">默认</el-tag>
                    </div>
                    <div class="form-status">
                        <el-tag :type="form.form_status === 1 ? 'success' : 'danger'" size="small">
                            {{ form.form_status === 1 ? '启用' : '禁用' }}
                        </el-tag>
                    </div>
                </div>
                <div class="form-description" v-if="form.form_description">
                    {{ form.form_description }}
                </div>
                <div class="form-meta">
                    <span><el-icon>
                            <Document />
                        </el-icon> 字段数: {{ form.field_count || 0 }}</span>
                    <span><el-icon>
                            <Clock />
                        </el-icon> 创建: {{ form.create_time }}</span>
                </div>
                <div class="form-actions">
                    <el-button type="primary" link @click="editForm(form)">
                        <el-icon>
                            <Edit />
                        </el-icon> 编辑
                    </el-button>
                    <el-button type="success" link @click="copyForm(form)">
                        <el-icon>
                            <CopyDocument />
                        </el-icon> 复制
                    </el-button>
                    <el-popconfirm :title="`确定删除表单「${form.form_name}」吗？`" @confirm="handleDelete(form.id)">
                        <template #reference>
                            <el-button type="danger" link>
                                <el-icon>
                                    <Delete />
                                </el-icon> 删除
                            </el-button>
                        </template>
                    </el-popconfirm>
                </div>
            </div>

            <div v-if="formList.length === 0 && !loading" class="empty-state">
                <el-empty description="暂无表单，点击右上角新增" />
            </div>
        </div>

        <!-- 分页 -->
        <div class="pagination-wrapper" v-if="pagination.total > pagination.limit">
            <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.limit"
                :total="pagination.total" :page-sizes="[10, 20, 50]" layout="total, sizes, prev, pager, next"
                @size-change="fetchData" @current-change="fetchData" />
        </div>

        <!-- 新增/编辑弹窗 -->
        <el-dialog v-model="dialogVisible" :title="dialogTitle" width="800px" destroy-on-close class="form-dialog">
            <el-form :model="formData" :rules="formRules" ref="formRef" label-width="100px">
                <el-form-item label="表单名称" prop="form_name" required>
                    <el-input v-model="formData.form_name" placeholder="请输入表单名称" />
                </el-form-item>
                <el-form-item label="表单描述">
                    <el-input v-model="formData.form_description" type="textarea" :rows="2" placeholder="请输入表单描述" />
                </el-form-item>
                <el-form-item label="设为默认">
                    <el-switch v-model="formData.is_default" :active-value="1" :inactive-value="0" />
                    <span class="form-tip">默认表单会自动关联到新建的预约商品</span>
                </el-form-item>
                <el-form-item label="表单状态">
                    <el-switch v-model="formData.form_status" :active-value="1" :inactive-value="0" active-text="启用"
                        inactive-text="禁用" />
                </el-form-item>

                <!-- 字段管理 -->
                <div class="fields-section">
                    <div class="fields-header">
                        <h4>表单字段</h4>
                        <div class="fields-header-actions">
                            <el-button type="primary" link @click="resetToDefaultFields">
                                <el-icon>
                                    <Refresh />
                                </el-icon> 恢复默认字段
                            </el-button>
                            <el-button type="primary" link @click="openFieldDialog">
                                <el-icon>
                                    <Plus />
                                </el-icon> 添加字段
                            </el-button>
                        </div>
                    </div>
                    <div class="fields-list" v-if="fields.length > 0">
                        <draggable v-model="fields" item-key="id" handle=".drag-handle" @end="onFieldSort">
                            <template #item="{ element, index }">
                                <div class="field-item">
                                    <div class="field-drag drag-handle">
                                        <el-icon>
                                            <Rank />
                                        </el-icon>
                                    </div>
                                    <div class="field-info">
                                        <div class="field-label">
                                            {{ element.field_label }}
                                            <el-tag v-if="element.is_required === 1" type="danger" size="small"
                                                class="required-tag">必填</el-tag>
                                        </div>
                                        <div class="field-meta">
                                            <span>字段名: {{ element.field_name }}</span>
                                            <span>类型: {{ getFieldTypeName(element.field_type) }}</span>
                                        </div>
                                    </div>
                                    <div class="field-actions">
                                        <el-button type="primary" link size="small" @click="editField(element, index)">
                                            <el-icon>
                                                <Edit />
                                            </el-icon>
                                        </el-button>
                                        <el-button type="danger" link size="small" @click="removeField(index)">
                                            <el-icon>
                                                <Delete />
                                            </el-icon>
                                        </el-button>
                                    </div>
                                </div>
                            </template>
                        </draggable>
                    </div>
                    <div v-else class="fields-empty">
                        <span>暂无字段，点击上方按钮添加或恢复默认字段</span>
                    </div>
                </div>
            </el-form>
            <template #footer>
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitForm" :loading="submitting">确 定</el-button>
            </template>
        </el-dialog>

        <!-- 字段编辑弹窗 -->
        <el-dialog v-model="fieldDialogVisible" :title="fieldDialogTitle" width="550px">
            <el-form :model="fieldForm" :rules="fieldRules" ref="fieldFormRef" label-width="100px">
                <el-form-item label="字段标签" prop="field_label" required>
                    <el-input v-model="fieldForm.field_label" placeholder="如：姓名、手机号" @input="autoGenerateFieldName" />
                </el-form-item>
                <el-form-item label="字段名称" prop="field_name" required>
                    <el-input v-model="fieldForm.field_name" placeholder="如：name、phone" />
                    <div class="form-tip">建议使用英文小写，系统会根据标签自动生成</div>
                </el-form-item>
                <el-form-item label="字段类型" prop="field_type" required>
                    <el-select v-model="fieldForm.field_type" placeholder="请选择字段类型" style="width: 100%"
                        @change="onFieldTypeChange">
                        <el-option label="单行文本" value="text" />
                        <el-option label="多行文本" value="textarea" />
                        <el-option label="数字" value="number" />
                        <el-option label="日期" value="date" />
                        <el-option label="日期时间" value="datetime" />
                        <el-option label="时间" value="time" />
                        <el-option label="下拉选择" value="select" />
                        <el-option label="单选按钮" value="radio" />
                        <el-option label="复选框" value="checkbox" />
                        <el-option label="文件上传" value="file" />
                    </el-select>
                </el-form-item>
                <el-form-item label="选项配置" v-if="['select', 'radio', 'checkbox'].includes(fieldForm.field_type)">
                    <el-select v-model="fieldForm.field_options" multiple filterable allow-create default-first-option
                        placeholder="请输入选项，按回车添加" style="width: 100%" />
                    <div class="form-tip">每输入一个选项后按回车确认</div>
                </el-form-item>
                <el-form-item label="是否必填">
                    <el-switch v-model="fieldForm.is_required" :active-value="1" :inactive-value="0" />
                    <span class="form-tip">开启后，用户必须填写该字段</span>
                </el-form-item>
                <el-form-item label="占位提示">
                    <el-input v-model="fieldForm.placeholder" placeholder="请输入占位提示" />
                    <div class="form-tip">显示在输入框内，提示用户如何填写</div>
                </el-form-item>
                <el-form-item label="默认值">
                    <el-input v-model="fieldForm.default_value" placeholder="请输入默认值" />
                    <div class="form-tip">用户未填写时自动填充的值</div>
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="fieldDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="saveField">确 定</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElLoading } from 'element-plus'
import Cookies from 'js-cookie'
import axios from 'axios'
import config from '@/config'
import draggable from 'vuedraggable'
import { Plus, Edit, Delete, Document, Clock, CopyDocument, Rank, Refresh } from '@element-plus/icons-vue'
import { pinyin } from 'pinyin-pro'

export default {
    name: 'ReservationFormPage',
    components: { draggable, Plus, Edit, Delete, Document, Clock, CopyDocument, Rank, Refresh },
    setup() {
        const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
        const agentId = ref(userInfo.agent_id || 0)
        const storeId = ref(userInfo.id)

        // 默认字段配置
        // 默认字段配置（只是示例，客户可以自由删除/编辑）
        const defaultFields = [
            { field_name: 'branch_name', field_label: '门店信息', field_type: 'text', is_required: 1, placeholder: '请选择门店', default_value: '', sort_order: 0, status: 1 },
            { field_name: 'store_branch_name', field_label: '分店信息', field_type: 'text', is_required: 0, placeholder: '请选择分店', default_value: '', sort_order: 1, status: 1 },
            { field_name: 'nickname', field_label: '昵称', field_type: 'text', is_required: 0, placeholder: '请输入您的昵称', default_value: '', sort_order: 2, status: 1 },
            { field_name: 'phone', field_label: '手机号', field_type: 'text', is_required: 1, placeholder: '请输入手机号', default_value: '', sort_order: 3, status: 1 },
            { field_name: 'reservation_time', field_label: '预约时间', field_type: 'datetime', is_required: 1, placeholder: '请选择预约时间', default_value: '', sort_order: 4, status: 1 },
            { field_name: 'people_count', field_label: '预约人数', field_type: 'number', is_required: 1, placeholder: '请输入预约人数', default_value: '1', sort_order: 5, status: 1 },
            { field_name: 'remark', field_label: '备注', field_type: 'textarea', is_required: 0, placeholder: '请输入备注信息', default_value: '', sort_order: 6, status: 1 }
        ]

        const loading = ref(false)
        const submitting = ref(false)
        const dialogVisible = ref(false)
        const fieldDialogVisible = ref(false)
        const isEditMode = ref(false)
        const editingFieldIndex = ref(-1)
        const formList = ref([])
        const formRef = ref(null)
        const fieldFormRef = ref(null)
        const searchKey = ref('')
        const statusFilter = ref(null)
        const fields = ref([])

        const pagination = reactive({
            page: 1,
            limit: 12,
            total: 0
        })

        const formData = reactive({
            id: null,
            form_name: '',
            form_description: '',
            form_status: 1,
            is_default: 0
        })

        const fieldForm = reactive({
            field_name: '',
            field_label: '',
            field_type: 'text',
            field_options: [],
            is_required: 0,
            placeholder: '',
            default_value: ''
        })

        const formRules = {
            form_name: [{ required: true, message: '请输入表单名称', trigger: 'blur' }]
        }

        const fieldRules = {
            field_label: [{ required: true, message: '请输入字段标签', trigger: 'blur' }],
            field_name: [
                { required: true, message: '请输入字段名称', trigger: 'blur' },
                { pattern: /^[a-zA-Z_][a-zA-Z0-9_]*$/, message: '字段名称只能包含字母、数字和下划线，且不能以数字开头', trigger: 'blur' }
            ],
            field_type: [{ required: true, message: '请选择字段类型', trigger: 'change' }]
        }

        const dialogTitle = ref('新增表单')
        const fieldDialogTitle = ref('添加字段')

        // 中文转拼音
        const chineseToPinyin = (chinese) => {
            if (!chinese) return ''
            // 使用 pinyin-pro 库转换
            const pinyinResult = pinyin(chinese, { toneType: 'none', type: 'array' })
            return pinyinResult.join('_').toLowerCase()
        }

        // 自动生成字段名称
        const autoGenerateFieldName = () => {
            if (fieldForm.field_label && !fieldForm.field_name) {
                const generatedName = chineseToPinyin(fieldForm.field_label)
                if (generatedName) {
                    fieldForm.field_name = generatedName
                }
            }
        }

        const getFieldTypeName = (type) => {
            const map = {
                text: '单行文本', textarea: '多行文本', number: '数字',
                date: '日期', datetime: '日期时间', time: '时间',
                select: '下拉选择', radio: '单选按钮', checkbox: '复选框', file: '文件上传'
            }
            return map[type] || type
        }

        const fetchData = async () => {
            loading.value = true
            try {
                const params = {
                    agent_id: agentId.value, store_id: storeId.value,
                    page: pagination.page, limit: pagination.limit,
                    search: searchKey.value, status: statusFilter.value
                }
                const response = await axios.get(`${config.BASE_URL}/shop/api/reservation_form_api.php`, { params })
                if (response.data.code === 200) {
                    formList.value = response.data.data.list
                    pagination.total = response.data.data.pagination.total
                }
            } catch (error) {
                console.error('获取表单列表失败:', error)
                ElMessage.error('获取数据失败')
            } finally {
                loading.value = false
            }
        }

        const resetSearch = () => {
            searchKey.value = ''
            statusFilter.value = null
            pagination.page = 1
            fetchData()
        }

        // 恢复默认字段
        const resetToDefaultFields = () => {
            fields.value = JSON.parse(JSON.stringify(defaultFields))
            ElMessage.success('已恢复默认字段')
        }

        const openAddDialog = () => {
            isEditMode.value = false
            dialogTitle.value = '新增表单'
            Object.assign(formData, { id: null, form_name: '', form_description: '', form_status: 1, is_default: 0 })
            // 设置默认字段
            fields.value = JSON.parse(JSON.stringify(defaultFields))
            dialogVisible.value = true
        }

        const editForm = async (form) => {
            isEditMode.value = true
            dialogTitle.value = '编辑表单'
            const loadingInstance = ElLoading.service({ text: '加载中...' })
            try {
                const response = await axios.get(`${config.BASE_URL}/shop/api/reservation_form_api.php`, {
                    params: { agent_id: agentId.value, store_id: storeId.value, id: form.id }
                })
                if (response.data.code === 200) {
                    const data = response.data.data
                    Object.assign(formData, {
                        id: data.id, form_name: data.form_name, form_description: data.form_description,
                        form_status: data.form_status, is_default: data.is_default
                    })
                    // 直接使用后端返回的字段，不需要额外标记
                    const loadedFields = data.fields || []
                    fields.value = loadedFields.length > 0 ? loadedFields : JSON.parse(JSON.stringify(defaultFields))
                    dialogVisible.value = true
                } else {
                    ElMessage.error('获取表单详情失败')
                }
            } catch (error) {
                console.error('获取表单详情失败:', error)
                ElMessage.error('获取表单详情失败')
            } finally {
                loadingInstance.close()
            }
        }

        const copyForm = async (form) => {
            const loadingInstance = ElLoading.service({ text: '复制中...' })
            try {
                const response = await axios.get(`${config.BASE_URL}/shop/api/reservation_form_api.php`, {
                    params: { agent_id: agentId.value, store_id: storeId.value, id: form.id }
                })
                if (response.data.code === 200) {
                    const data = response.data.data
                    const copyData = {
                        agent_id: agentId.value, store_id: storeId.value,
                        form_name: `${data.form_name}(副本)`, form_description: data.form_description,
                        form_status: 1, is_default: 0,
                        fields: data.fields || []
                    }
                    const createResponse = await axios.post(`${config.BASE_URL}/shop/api/reservation_form_api.php`, copyData)
                    if (createResponse.data.code === 200) {
                        ElMessage.success('复制成功')
                        fetchData()
                    } else {
                        ElMessage.error(createResponse.data.message || '复制失败')
                    }
                }
            } catch (error) {
                console.error('复制失败:', error)
                ElMessage.error('复制失败')
            } finally {
                loadingInstance.close()
            }
        }

        const submitForm = async () => {
            if (!formRef.value) return
            await formRef.value.validate(async (valid) => {
                if (!valid) return
                submitting.value = true
                const loadingInstance = ElLoading.service({ text: '保存中...' })
                try {
                    const payload = {
                        agent_id: agentId.value, store_id: storeId.value,
                        form_name: formData.form_name, form_description: formData.form_description,
                        form_status: formData.form_status, is_default: formData.is_default,
                        fields: fields.value  // 直接保存，不需要过滤
                    }
                    let response
                    if (isEditMode.value) {
                        payload.id = formData.id
                        response = await axios.put(`${config.BASE_URL}/shop/api/reservation_form_api.php`, payload)
                    } else {
                        response = await axios.post(`${config.BASE_URL}/shop/api/reservation_form_api.php`, payload)
                    }
                    if (response.data.code === 200) {
                        ElMessage.success(isEditMode.value ? '更新成功' : '创建成功')
                        dialogVisible.value = false
                        fetchData()
                    } else {
                        ElMessage.error(response.data.message || '操作失败')
                    }
                } catch (error) {
                    console.error('提交失败:', error)
                    ElMessage.error('操作失败')
                } finally {
                    submitting.value = false
                    loadingInstance.close()
                }
            })
        }

        const handleDelete = async (id) => {
            const loadingInstance = ElLoading.service({ text: '删除中...' })
            try {
                const response = await axios.delete(`${config.BASE_URL}/shop/api/reservation_form_api.php`, {
                    data: { id, store_id: storeId.value }
                })
                if (response.data.code === 200) {
                    ElMessage.success('删除成功')
                    fetchData()
                } else {
                    ElMessage.error(response.data.message || '删除失败')
                }
            } catch (error) {
                console.error('删除失败:', error)
                ElMessage.error('删除失败')
            } finally {
                loadingInstance.close()
            }
        }

        const openFieldDialog = () => {
            editingFieldIndex.value = -1
            fieldDialogTitle.value = '添加字段'
            Object.assign(fieldForm, {
                field_name: '', field_label: '', field_type: 'text',
                field_options: [], is_required: 0, placeholder: '', default_value: ''
            })
            fieldDialogVisible.value = true
        }

        const editField = (field, index) => {
            editingFieldIndex.value = index
            fieldDialogTitle.value = '编辑字段'
            Object.assign(fieldForm, {
                field_name: field.field_name, field_label: field.field_label,
                field_type: field.field_type, field_options: field.field_options || [],
                is_required: field.is_required, placeholder: field.placeholder || '',
                default_value: field.default_value || ''
            })
            fieldDialogVisible.value = true
        }

        const saveField = async () => {
            if (!fieldFormRef.value) return
            await fieldFormRef.value.validate(async (valid) => {
                if (!valid) return
                const fieldData = {
                    field_name: fieldForm.field_name,
                    field_label: fieldForm.field_label,
                    field_type: fieldForm.field_type,
                    field_options: fieldForm.field_options,
                    is_required: fieldForm.is_required,
                    placeholder: fieldForm.placeholder,
                    default_value: fieldForm.default_value,
                    sort_order: editingFieldIndex.value === -1 ? fields.value.length : editingFieldIndex.value,
                    status: 1,
                    is_system: 0  // 自定义字段
                }
                if (editingFieldIndex.value === -1) {
                    fields.value.push(fieldData)
                } else {
                    fields.value[editingFieldIndex.value] = fieldData
                }
                fieldDialogVisible.value = false
                ElMessage.success(editingFieldIndex.value === -1 ? '添加成功' : '更新成功')
            })
        }

        const removeField = (index) => {
            fields.value.splice(index, 1)
        }

        const onFieldSort = () => {
            fields.value.forEach((field, index) => { field.sort_order = index })
        }

        const onFieldTypeChange = () => {
            if (!['select', 'radio', 'checkbox'].includes(fieldForm.field_type)) {
                fieldForm.field_options = []
            }
        }

        onMounted(() => {
            fetchData()
        })

        return {
            loading, submitting, dialogVisible, fieldDialogVisible, isEditMode,
            formList, formRef, fieldFormRef, searchKey, statusFilter, fields,
            pagination, formData, fieldForm, formRules, fieldRules,
            dialogTitle, fieldDialogTitle, getFieldTypeName,
            fetchData, resetSearch, openAddDialog, editForm, copyForm,
            submitForm, handleDelete, openFieldDialog, editField, saveField,
            removeField, onFieldSort, onFieldTypeChange, resetToDefaultFields,
            autoGenerateFieldName
        }
    }
}
</script>

<style scoped lang="scss">
.reservation-form {
    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 24px;

        .page-title {
            font-size: 24px;
            font-weight: 600;
            color: #1a1a2e;
            margin-bottom: 4px;
        }

        .page-desc {
            font-size: 14px;
            color: #8c8c8c;
        }
    }

    .filter-bar {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
        margin-bottom: 24px;
    }

    .form-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
        gap: 20px;

        .form-card {
            background: #fff;
            border-radius: 16px;
            padding: 20px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
            transition: all 0.3s;

            &:hover {
                transform: translateY(-4px);
                box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
            }

            .form-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 12px;

                .form-title {
                    display: flex;
                    align-items: center;
                    gap: 8px;

                    h3 {
                        font-size: 16px;
                        font-weight: 600;
                        margin: 0;
                    }
                }
            }

            .form-description {
                color: #666;
                font-size: 13px;
                margin-bottom: 12px;
                line-height: 1.4;
            }

            .form-meta {
                display: flex;
                gap: 16px;
                font-size: 12px;
                color: #8c8c8c;
                margin-bottom: 16px;

                .el-icon {
                    margin-right: 4px;
                    vertical-align: middle;
                }
            }

            .form-actions {
                display: flex;
                gap: 12px;
                padding-top: 12px;
                border-top: 1px solid #f0f0f0;
            }
        }
    }

    .empty-state {
        grid-column: 1 / -1;
        padding: 60px 0;
    }

    .pagination-wrapper {
        display: flex;
        justify-content: flex-end;
        margin-top: 24px;
    }

    .form-tip {
        font-size: 12px;
        color: #8c8c8c;
        margin-top: 4px;
    }

    .fields-section {
        margin-top: 20px;
        border-top: 1px solid #ebeef5;
        padding-top: 16px;

        .fields-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 16px;

            h4 {
                margin: 0;
                font-size: 14px;
                font-weight: 500;
            }

            .fields-header-actions {
                display: flex;
                gap: 12px;
            }
        }

        .fields-list {
            .field-item {
                display: flex;
                align-items: center;
                padding: 12px;
                background: #f5f7fa;
                border-radius: 8px;
                margin-bottom: 8px;

                .field-drag {
                    cursor: move;
                    margin-right: 12px;
                    color: #8c8c8c;
                }

                .field-info {
                    flex: 1;

                    .field-label {
                        font-weight: 500;
                        margin-bottom: 4px;

                        .required-tag {
                            margin-left: 8px;
                        }

                        .system-tag {
                            margin-left: 8px;
                        }
                    }

                    .field-meta {
                        font-size: 12px;
                        color: #8c8c8c;
                        display: flex;
                        gap: 16px;
                    }
                }

                .field-actions {
                    display: flex;
                    gap: 8px;
                }
            }
        }

        .fields-empty {
            text-align: center;
            padding: 30px;
            color: #8c8c8c;
            background: #f5f7fa;
            border-radius: 8px;
        }
    }
}
</style>